"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  FaRocket,
  FaCode,
  FaProjectDiagram,
  FaServer,
  FaTrophy,
  FaJs,
  FaNodeJs,
  FaReact,
} from "react-icons/fa";

/* ===== Palette ===== */
const colorPalette = {
  cotton: "#EDEBDD",
  cherryRed: "#B10100",
  maroon: "#630000",
  noirBlack: "#1B1717",
};

/* ===== Pointer Highlight Component ===== */
function PointerHighlight({
  children,
  rectangleClassName = "",
  pointerClassName = "",
  containerClassName = ""
}) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(false);
 useEffect(() => {
    const container = containerRef.current;
    
    if (container) {
      const { width, height } = container.getBoundingClientRect();
      setDimensions({ width, height });
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    });

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    if (container) {
      resizeObserver.observe(container);
      intersectionObserver.observe(container);
    }

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
        intersectionObserver.unobserve(container);
      }
    };
  }, []);

  return (
    <div className={`relative w-fit ${containerClassName}`} ref={containerRef}>
      {children}
      {dimensions.width > 0 && dimensions.height > 0 && (
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'scale(1)' : 'scale(0.95)',
            transformOrigin: '0 0',
            transition: 'opacity 0.5s ease-out, transform 0.5s ease-out'
          }}
        >
          <div
            className={`absolute inset-0 border-2 ${rectangleClassName}`}
            style={{
              width: isVisible ? `${dimensions.width}px` : '0px',
              height: isVisible ? `${dimensions.height}px` : '0px',
              borderColor: colorPalette.cherryRed,
              transition: 'width 1s ease-in-out, height 1s ease-in-out',
            }}
          />
          <div
            className="pointer-events-none absolute"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: `translate(${isVisible ? dimensions.width + 4 : 0}px, ${isVisible ? dimensions.height + 4 : 0}px) rotate(-90deg)`,
              transition: 'opacity 0.1s ease-in-out 1s, transform 1s ease-in-out',
            }}
          >
            <Pointer className={`h-5 w-5 ${pointerClassName}`} style={{ color: colorPalette.cherryRed }} />
          </div>
        </div>
      )}
    </div>
  );
}

const Pointer = ({ className, style, ...props }) => {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 16 16"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      {...props}
    >
      <path d="M14.082 2.182a.5.5 0 0 1 .103.557L8.528 15.467a.5.5 0 0 1-.917-.007L5.57 10.694.803 8.652a.5.5 0 0 1-.006-.916l12.728-5.657a.5.5 0 0 1 .556.103z"></path>
    </svg>
  );
};

/* ===== Timeline Data ===== */
const timelineData = [
  {
    title: "2022",
    content: (
      <div
        className="backdrop-blur-sm border border-black/10 rounded-lg sm:rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 z-20 w-full p-4 sm:p-5 md:p-6 lg:p-8 min-h-[160px] md:min-h-[180px] lg:min-h-[200px] hover:border-black/20"
        style={{ backgroundColor: colorPalette.cotton }}
      >
        <div className="flex items-center gap-3 mb-3">
          <FaRocket className="text-base" style={{ color: colorPalette.cherryRed }} />
          <h3
            className="text-xl md:text-2xl lg:text-3xl font-semibold leading-tight"
            style={{ fontFamily: "GRADIOO", color: colorPalette.noirBlack }}
          >
            Started My Journey
          </h3>
        </div>
        <span
          className="inline-block text-sm md:text-base px-3 py-1 rounded-full border mb-4"
          style={{
            fontFamily: "HetigonVintage",
            backgroundColor: `${colorPalette.cherryRed}20`,
            color: colorPalette.cherryRed,
            borderColor: `${colorPalette.cherryRed}50`,
          }}
        >
          2022
        </span>
        <p
          className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed"
          style={{ color: `${colorPalette.noirBlack}CC` }}
        >
          Began learning web development and discovered my passion for coding.
        </p>
      </div>
    ),
  },
  {
    title: "2023",
    content: (
      <div
        className="backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 z-20 w-full p-4 sm:p-5 md:p-6 lg:p-8 min-h-[160px] md:min-h-[180px] lg:min-h-[200px] hover:border-white/40"
        style={{ backgroundColor: colorPalette.cherryRed }}
      >
        <div className="flex items-center gap-3 mb-3">
          <FaCode className="text-base" style={{ color: colorPalette.cotton }} />
          <h3
            className="text-xl md:text-2xl lg:text-3xl font-semibold leading-tight"
            style={{ fontFamily: "GRADIOO", color: colorPalette.cotton }}
          >
            Full Stack Foundations
          </h3>
        </div>
        <span
          className="inline-block text-sm md:text-base px-3 py-1 rounded-full border mb-4"
          style={{
            fontFamily: "HetigonVintage",
            backgroundColor: `${colorPalette.cotton}20`,
            color: colorPalette.cotton,
            borderColor: `${colorPalette.cotton}50`,
          }}
        >
          2023
        </span>
        <p
          className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed"
          style={{ color: `${colorPalette.cotton}DD` }}
        >
          Mastered{" "}
          <span className="inline-flex items-center gap-1 text-yellow-200">
            <FaJs className="text-xs" /> JavaScript
          </span>
          ,{" "}
          <span className="inline-flex items-center gap-1 text-green-200">
            <FaNodeJs className="text-xs" /> Node.js
          </span>
          , and{" "}
          <span className="inline-flex items-center gap-1 text-cyan-200">
            <FaReact className="text-xs" /> React.js
          </span>
          , along with backend basics.
        </p>
      </div>
    ),
  },
  {
    title: "2024",
    content: (
      <div
        className="backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 z-20 w-full p-4 sm:p-5 md:p-6 lg:p-8 min-h-[160px] md:min-h-[180px] lg:min-h-[200px] hover:border-white/40"
        style={{ backgroundColor: colorPalette.maroon }}
      >
        <div className="flex items-center gap-3 mb-3">
          <FaProjectDiagram className="text-base" style={{ color: colorPalette.cotton }} />
          <h3
            className="text-xl md:text-2xl lg:text-3xl font-semibold leading-tight"
            style={{ fontFamily: "GRADIOO", color: colorPalette.cotton }}
          >
            Real Projects & Freelance
          </h3>
        </div>
        <span
          className="inline-block text-sm md:text-base px-3 py-1 rounded-full border mb-4"
          style={{
            fontFamily: "HetigonVintage",
            backgroundColor: `${colorPalette.cotton}20`,
            color: colorPalette.cotton,
            borderColor: `${colorPalette.cotton}50`,
          }}
        >
          2024
        </span>
        <p
          className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed"
          style={{ color: `${colorPalette.cotton}DD` }}
        >
          Built real-world apps, joined freelance platforms, and grew confidence.
        </p>
      </div>
    ),
  },
  {
    title: "2025",
    content: (
      <div
        className="backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 z-20 w-full p-4 sm:p-5 md:p-6 lg:p-8 min-h-[160px] md:min-h-[180px] lg:min-h-[200px] hover:border-white/40"
        style={{ backgroundColor: colorPalette.noirBlack }}
      >
        <div className="flex items-center gap-3 mb-3">
          <FaServer className="text-base" style={{ color: colorPalette.cotton }} />
          <h3
            className="text-xl md:text-2xl lg:text-3xl font-semibold leading-tight"
            style={{ fontFamily: "GRADIOO", color: colorPalette.cotton }}
          >
            Advanced Stack
          </h3>
        </div>
        <span
          className="inline-block text-sm md:text-base px-3 py-1 rounded-full border mb-4"
          style={{
            fontFamily: "HetigonVintage",
            backgroundColor: `${colorPalette.cherryRed}20`,
            color: colorPalette.cherryRed,
            borderColor: `${colorPalette.cherryRed}50`,
          }}
        >
          2025
        </span>
        <p
          className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed"
          style={{ color: `${colorPalette.cotton}DD` }}
        >
          Deep dived into{" "}
          <span className="inline-flex items-center gap-1 text-cyan-300">
            <FaReact className="text-xs" /> Next.js
          </span>
          , APIs, deployment, and system design.
        </p>
      </div>
    ),
  },
  {
    title: "Future",
    content: (
      <div
        className="backdrop-blur-sm border border-black/10 rounded-lg sm:rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 z-20 w-full p-4 sm:p-5 md:p-6 lg:p-8 min-h-[160px] md:min-h-[180px] lg:min-h-[200px] hover:border-black/20"
        style={{ backgroundColor: colorPalette.cotton }}
      >
        <div className="flex items-center gap-3 mb-3">
          <FaTrophy className="text-base" style={{ color: colorPalette.cherryRed }} />
          <h3
            className="text-xl md:text-2xl lg:text-3xl font-semibold leading-tight"
            style={{ fontFamily: "GRADIOO", color: colorPalette.noirBlack }}
          >
            Become Top Developer
          </h3>
        </div>
        <span
          className="inline-block text-sm md:text-base px-3 py-1 rounded-full border mb-4"
          style={{
            fontFamily: "HetigonVintage",
            backgroundColor: `${colorPalette.cherryRed}20`,
            color: colorPalette.cherryRed,
            borderColor: `${colorPalette.cherryRed}50`,
          }}
        >
          Future
        </span>
        <p
          className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed"
          style={{ color: `${colorPalette.noirBlack}CC` }}
        >
          Grow a strong portfolio, work with top companies, and create tech impact.
        </p>
      </div>
    ),
  },
];

/* ===== Professional Timeline Component ===== */
export const Timeline = ({ data = timelineData }) => {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [ref]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const containerTop = rect.top;
      const containerHeight = rect.height;
      const windowHeight = window.innerHeight;

      // Calculate scroll progress
      const startOffset = windowHeight * 0.1; // start 10% from top
      const endOffset = windowHeight * 0.5; // end 50% from top

      let progress = 0;
      if (containerTop <= startOffset && containerTop > -(containerHeight - endOffset)) {
        progress = Math.abs(containerTop - startOffset) / (containerHeight - endOffset + startOffset);
      } else if (containerTop <= -(containerHeight - endOffset)) {
        progress = 1;
      }

      progress = Math.max(0, Math.min(1, progress));
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const lineHeight = height * scrollProgress;
  const lineOpacity = Math.min(scrollProgress * 10, 1);

  return (
    <>
      <style jsx>{`
        @font-face {
          font-family: "GRADIOO";
          src: url("data:font/truetype;base64,") format("truetype");
        }
        @font-face {
          font-family: "HetigonVintage";
          src: url("data:font/opentype;base64,") format("opentype");
        }
      `}</style>

      <div
        className="w-full font-sans md:px-10"
        style={{ backgroundColor: colorPalette.cotton }}
        ref={containerRef}
      >
        <div className="max-w-7xl mx-auto py-20 px-4 md:px-8 lg:px-10">
          <PointerHighlight containerClassName="mb-4">
            <h2 
              className="text-lg md:text-4xl max-w-4xl"
              style={{ fontFamily: "GRADIOO", color: colorPalette.noirBlack }}
            >
              Changelog from my journey
            </h2>
          </PointerHighlight>
          <p
            className="text-sm md:text-base max-w-sm"
            style={{ fontFamily: "HetigonVintage", color: `${colorPalette.noirBlack}AA` }}
          >
            I&apos;ve been working on my development skills for the past few years. Here&apos;s
            a timeline of my journey.
          </p>
        </div>

        <div ref={ref} className="relative max-w-7xl mx-auto pb-20">
          {data.map((item, index) => (
            <div key={index} className="flex justify-start pt-10 md:pt-40 md:gap-10">
              <div className="sticky flex flex-col md:flex-row z-40 items-center top-40 self-start max-w-xs lg:max-w-sm md:w-full">
                <div
                  className="h-10 absolute left-3 md:left-3 w-10 rounded-full flex items-center justify-center border-2"
                  style={{ 
                    backgroundColor: colorPalette.cotton,
                    borderColor: colorPalette.cherryRed
                  }}
                >
                  <div
                    className="h-4 w-4 rounded-full border-2 p-2"
                    style={{ 
                      backgroundColor: colorPalette.cherryRed,
                      borderColor: `${colorPalette.cherryRed}80`,
                      filter: `drop-shadow(0 0 8px ${colorPalette.cherryRed}60)`
                    }}
                  />
                </div>
                <div className="hidden md:block md:pl-20">
                  <PointerHighlight>
                    <h3
                      className="text-xl md:text-5xl font-bold"
                      style={{ 
                        fontFamily: "GRADIOO", 
                        color: `${colorPalette.noirBlack}80` 
                      }}
                    >
                      {item.title}
                    </h3>
                  </PointerHighlight>
                </div>
              </div>

              <div className="relative pl-20 pr-4 md:pl-4 w-full">
                <div className="md:hidden block mb-4">
                  <PointerHighlight>
                    <h3
                      className="text-2xl text-left font-bold"
                      style={{ 
                        fontFamily: "GRADIOO", 
                        color: `${colorPalette.noirBlack}80` 
                      }}
                    >
                      {item.title}
                    </h3>
                  </PointerHighlight>
                </div>
                {item.content}
              </div>
            </div>
          ))}

          {/* Animated Timeline Line */}
          <div
            style={{ height: `${height}px` }}
            className="absolute md:left-8 left-8 top-0 overflow-hidden w-[2px]"
          >
            {/* Background line */}
            <div 
              className="absolute inset-x-0 top-0 w-[2px] h-full opacity-30"
              style={{
                background: `linear-gradient(to bottom, transparent 0%, ${colorPalette.cherryRed}40 10%, ${colorPalette.cherryRed}40 90%, transparent 100%)`
              }}
            />
            
            {/* Animated progress line */}
            <div
              className="absolute inset-x-0 top-0 w-[2px] transition-all duration-300 ease-out"
              style={{
                height: `${lineHeight}px`,
                opacity: lineOpacity,
                background: `linear-gradient(to top, ${colorPalette.cherryRed} 0%, ${colorPalette.maroon} 50%, ${colorPalette.cherryRed} 100%)`,
                filter: `drop-shadow(0 0 4px ${colorPalette.cherryRed}60)`,
                borderRadius: '1px'
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

function AboutSection() {
  return <Timeline />;
}

export default AboutSection;